import { usePointerParallax } from '@/hooks/ui/usePointerParallax';

/**
 * The page lattice, in both themes.
 *
 * Modelled on the landing page's grid
 * (`xonera-landing-page/src/components/ambient/InstrumentGrid.tsx`). Four ideas
 * carried over:
 *
 *  1. TWO TIERS — a fine 16px grid under a heavy 96px one. A single pitch reads
 *     as graph paper; two read as instrumentation.
 *  2. CROSSHAIR TICKS on the major intersections, drawn as an SVG tile because
 *     linear-gradients tie their repeat interval to the arm length.
 *  3. A POINTER SPOTLIGHT — the same grid, brighter, revealed through a ~300px
 *     radial mask that follows the cursor, so the light is an INTERACTION
 *     rather than a static wash.
 *  4. The grid QUIETENS down the centre column where body copy lives and stays
 *     at full strength in the margins.
 *
 * (4) is done differently here, and deliberately. The landing page paints a
 * white gradient over the centre; that works only because its ground is white.
 * This app's light ground is a blue-50 -> indigo-50 gradient, so a white scrim
 * showed up as a bright vertical band down the middle of every page. Masking
 * the lattice instead removes nothing but the lattice — there is no painted
 * layer to mismatch the ground, and it behaves identically in both themes.
 */

interface GridTheme {
  /** Resting line colour, `rgb()`-compatible triplet source. */
  line: string;
  /** Crosshair stroke, hex without `#`. */
  tickHex: string;
  minor: number;
  major: number;
  tick: number;
  /** Pointer-revealed copy: brighter, and may differ in hue. */
  spotLine: string;
  spotHex: string;
  spot: number;
  spotTick: number;
}

/**
 * LIGHT — the landing page's own numbers, and the reference the dark ramp is
 * tuned against. See the note above DARK for why the two alpha sets differ.
 *
 * Measured contrast of each layer against `--ground` (#E1E8FD):
 * minor 1.07:1 · major 1.13:1 · ticks 1.23:1 · spotlight 1.45:1 / 2.39:1.
 */
const LIGHT: GridTheme = {
  line: 'var(--grid-line)', // 91 89 244 indigo (#5B59F4)
  tickHex: '6366F1',
  minor: 0.055,
  major: 0.1,
  tick: 0.18,
  spotLine: '99 102 241',
  spotHex: '6366F1',
  spot: 0.32,
  spotTick: 0.7,
};

/**
 * DARK — violet lattice. The spotlight rises to `--primary` lavender, so the
 * cursor reveals a LIT version of the resting hue, not a second colour.
 *
 * WHY THESE ALPHAS ARE NOT THE LIGHT ONES. Alpha is not a look; it is a
 * distance from the ground, and the two grounds are at opposite ends of the
 * scale (#E1E8FD vs #0F1015). Violet at light's 5.5% over near-black is
 * invisible, so dark needs roughly double the alpha to land at the same
 * perceived weight. Matching the NUMBERS would delete the lattice; matching
 * the CONTRAST is what makes the themes read as one design.
 *
 * So these are solved against the light ramp above rather than picked by eye.
 * Measured against dark `--ground`, each layer now sits within a few percent
 * of its light counterpart:
 *
 *              light      dark (was)     dark (now)
 *   minor      1.07:1     1.10:1         1.08:1
 *   major      1.13:1     1.24:1         1.14:1
 *   ticks      1.23:1     2.03:1  ←      1.25:1
 *   spot       1.45:1     3.65:1  ←      1.46:1
 *   spot tick  2.39:1     7.73:1  ←      2.48:1
 *
 * The three arrows are what made dark read as a different, heavier design
 * rather than the same one after dark. The crosshairs were carrying 65% more
 * contrast than light's and the pointer spotlight over 2.5x, so moving the
 * cursor lit up a grid that light mode only ever whispers.
 */
const DARK: GridTheme = {
  line: 'var(--grid-line)', // 136 70 228 violet (#8846E4)
  tickHex: '7E5FD6',
  minor: 0.1,
  major: 0.16,
  tick: 0.22,
  spotLine: '177 152 235',
  spotHex: 'B198EB',
  spot: 0.22,
  spotTick: 0.45,
};

/** 96x96 tile with a 7px crosshair at its centre. Offsetting the layer by half
 *  a cell lands every cross exactly on a major intersection. */
const crosshairTile = (hex: string, opacity: number) =>
  `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='96' height='96'%3E%3Cpath d='M44.5,48.5 H51.5 M48.5,44.5 V51.5' stroke='%23${hex}' stroke-opacity='${opacity}' stroke-width='1' fill='none'/%3E%3C/svg%3E")`;

/** Parallax: the coarser layer travels further, so the two read as separate
 *  depths rather than one texture. Static without --px/--py (reduced motion). */
/**
 * Exported for `core/animations/GridGlyph404`, which lights individual lattice
 * cells to spell a 404 on the error page. It must drift by the SAME 3px as the
 * minor lattice or it slips against the grid it is supposed to be part of —
 * which is the one defect there that would pass every test and still look
 * wrong. The palette is deliberately NOT exported with it: that layer reads
 * `--brand-*` through Tailwind `dark:` variants, so it needs no JS theme copy.
 */
export const drift = (px: number) => ({
  transform: `translate3d(calc(var(--px, 0) * ${px}px), calc(var(--py, 0) * ${px}px), 0)`,
  willChange: 'transform',
});

/** Radial mask centred on the pointer, so a layer is revealed only near it. */
const spot = (radius: number, mid?: string) => {
  const img = `radial-gradient(circle ${radius}px at var(--gx, 50%) var(--gy, 45%), #000 0%, ${mid ?? ''}transparent ${radius === 300 ? '70%' : '68%'})`;
  return { maskImage: img, WebkitMaskImage: img };
};

/** Softens the lattice at the viewport edges so it never ends on a hard line. */
const VIGNETTE =
  'radial-gradient(ellipse 92% 90% at 50% 45%, #000 58%, rgba(0,0,0,0.4) 82%, transparent 100%)';

/** Fades the lattice down the centre column, where body copy sits. Applied as a
 *  MASK on a nested wrapper: nesting two masked elements multiplies them, which
 *  avoids `mask-composite` and paints nothing over the page. */
const CENTRE_FADE =
  'linear-gradient(90deg, #000 0%, rgba(0,0,0,0.45) 24%, rgba(0,0,0,0.32) 50%, rgba(0,0,0,0.45) 76%, #000 100%)';

/** One themed layer stack. Rendered twice, visibility-scoped, rather than
 *  driven by CSS variables: the crosshair colour lives inside an SVG data URI,
 *  and CSS cannot interpolate a custom property into a `url()` string. The
 *  hidden copy is `display:none`, so it is never painted or composited. */
function GridLayers({
  t,
  className,
  centreFade,
}: {
  t: GridTheme;
  className: string;
  centreFade: boolean;
}) {
  const line = (a: number) => `rgb(${t.line} / ${a})`;
  const spotLine = (a: number) => `rgb(${t.spotLine} / ${a})`;

  const minorGrid = (a: number, c = line) => ({
    backgroundImage: `linear-gradient(${c(a)} 1px, transparent 1px), linear-gradient(90deg, ${c(a)} 1px, transparent 1px)`,
    backgroundSize: '16px 16px',
  });

  const majorGrid = (a: number, cross: number, c = line, hex = t.tickHex) => ({
    backgroundImage: `${crosshairTile(hex, cross)}, linear-gradient(${c(a)} 1px, transparent 1px), linear-gradient(90deg, ${c(a)} 1px, transparent 1px)`,
    backgroundSize: '96px 96px, 96px 96px, 96px 96px',
    backgroundPosition: '48px 48px, 0 0, 0 0',
  });

  return (
    <div
      className={`absolute inset-0 ${className}`}
      style={{ maskImage: VIGNETTE, WebkitMaskImage: VIGNETTE }}
    >
      <div
        className='absolute inset-0'
        style={
          centreFade
            ? { maskImage: CENTRE_FADE, WebkitMaskImage: CENTRE_FADE }
            : undefined
        }
      >
        {/* Minor grid, the further layer */}
        <div
          className='absolute -inset-8'
          style={{ ...minorGrid(t.minor), ...drift(3) }}
        />
        {/* Major grid + crosshairs, the nearer layer */}
        <div
          className='absolute -inset-8'
          style={{ ...majorGrid(t.major, t.tick), ...drift(7) }}
        />
        {/* Spotlight: the same grid, brighter, revealed only near the pointer */}
        <div
          className='absolute -inset-8'
          style={{
            ...majorGrid(t.spot, t.spotTick, spotLine, t.spotHex),
            ...drift(7),
            ...spot(300, 'rgba(0,0,0,0.5) 42%, '),
          }}
        />
        <div
          className='absolute -inset-8'
          style={{
            ...minorGrid(t.spot * 0.5, spotLine),
            ...drift(3),
            ...spot(230),
          }}
        />
      </div>
    </div>
  );
}

interface InstrumentGridProps {
  /**
   * Fade the lattice down the centre column (idea 4 above). On by default —
   * this is the app's shipping look.
   *
   * Exists as a prop only so `Foundation/Backdrop` can show the two side
   * by side; nothing in the app passes it. Turning it off is not a supported
   * configuration: full-strength lattice runs straight under body copy, which
   * is the legibility problem the fade was added to solve.
   */
  centreFade?: boolean;
}

export function InstrumentGrid({
  centreFade = true,
}: InstrumentGridProps = {}) {
  usePointerParallax();

  return (
    <div
      aria-hidden
      className='pointer-events-none fixed inset-0 z-0 overflow-hidden'
    >
      <GridLayers t={LIGHT} className='dark:hidden' centreFade={centreFade} />
      <GridLayers
        t={DARK}
        className='hidden dark:block'
        centreFade={centreFade}
      />
    </div>
  );
}
